import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc, 
  doc, 
  Timestamp, 
  QueryConstraint,
  getCountFromServer,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { startOfDay, endOfDay } from 'date-fns';

export interface TaskItem {
  id: string;
  task: string;
  userId: string;
  leadId: string;
  createdAt: Timestamp;
  dueDate: Timestamp;
  isDone: boolean;
  assignedBy?: string;
  leadName?: string;
  leadNumber?: string;
  assignerName?: string;
  assigneeName?: string;
  status: 'today' | 'upcoming' | 'overdue';
}

export interface TaskMetrics {
  totalTasks: number;
  dueToday: number;
  upcoming: number;
  overdue: number;
}

export interface TaskFilters {
  companyId: string;
  userIds?: string[];
  startTime?: string | null;
  endTime?: string | null;
  currentScreen: string;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  currentUserId: string;
}

// Helper function to process task data with lead and user information
const processTaskData = async (docSnapshot: any, companyId: string): Promise<TaskItem> => {
  const data = docSnapshot.data();
  
  // Fetch lead information
  let leadName = 'Unknown Lead';
  let leadNumber = 'No Number';
  if (data.leadId) {
    try {
      const leadDocRef = doc(db, `companies/${companyId}/leads`, data.leadId);
      const leadDoc = await getDoc(leadDocRef);
      if (leadDoc.exists()) {
        const leadData = leadDoc.data();
        leadName = leadData?.name || 'Unknown Lead';
        leadNumber = leadData?.number || 'No Number';
      }
    } catch (error) {
      console.error('Error fetching lead:', error);
    }
  }

  // Fetch assigned user name
  let assigneeName = 'Unknown User';
  if (data.userId) {
    try {
      const userDocRef = doc(db, 'users', data.userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        assigneeName = userDoc.data()?.name || 'Unknown User';
      }
    } catch (error) {
      console.error('Error fetching assignee:', error);
    }
  }

  // Fetch assigner name
  let assignerName = 'Unknown Assigner';
  if (data.assignedBy) {
    try {
      const assignerDocRef = doc(db, 'users', data.assignedBy);
      const assignerDoc = await getDoc(assignerDocRef);
      if (assignerDoc.exists()) {
        assignerName = assignerDoc.data()?.name || 'Unknown Assigner';
      }
    } catch (error) {
      console.error('Error fetching assigner:', error);
    }
  }

  // Determine status based on due date
  const dueDate = data.dueDate.toDate();
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  
  let status: 'today' | 'upcoming' | 'overdue' = 'upcoming';
  
  if (dueDate < todayStart) {
    status = 'overdue';
  } else if (dueDate >= todayStart && dueDate <= todayEnd) {
    status = 'today';
  } else {
    status = 'upcoming';
  }

  return {
    id: docSnapshot.id,
    task: data.task,
    userId: data.userId,
    leadId: data.leadId,
    createdAt: data.createdAt,
    dueDate: data.dueDate,
    isDone: data.isDone,
    assignedBy: data.assignedBy,
    leadName,
    leadNumber,
    assignerName,
    assigneeName,
    status
  };
};

// Fetch task metrics (counts)
export const fetchTaskMetrics = async (filters: TaskFilters): Promise<TaskMetrics> => {
  try {
    const tasksRef = collection(db, `companies/${filters.companyId}/tasks`);
    
    // Base conditions for pending tasks
    let baseConditions: QueryConstraint[] = [
      where('isDone', '==', false)
    ];

    // Apply user filters based on role and selection
    if (filters.userIds && filters.userIds.length > 0) {
      console.log("Applying user filters for task metrics:", filters.userIds);
      if (filters.userIds.length === 1) {
        // For single user
        baseConditions.push(where('userId', '==', filters.userIds[0]));
        console.log("Using single user filter:", filters.userIds[0]);
      } else if (filters.userIds.length <= 10) {
        // For multiple users (Firebase 'in' query supports up to 10 values)
        baseConditions.push(where('userId', 'in', filters.userIds));
        console.log("Using multiple user filter (in query):", filters.userIds);
      } else {
        // For more than 10 users, we need to fetch all tasks and filter in memory
        // This is less efficient but necessary due to Firebase limitations
        console.log("Using memory filtering for many users:", filters.userIds.length);
        return await fetchTaskMetricsForManyUsers(filters);
      }
    } else {
      console.log("No specific users selected, applying role-based filtering");
      // Apply role-based filtering when no specific users selected
      if (filters.isAdmin && filters.currentScreen === 'Admin') {
        // Admin sees all tasks (no additional filter needed)
        console.log("Admin user - showing all tasks");
      } else if (filters.isManager && filters.currentScreen === 'Manager') {
        // Manager sees tasks assigned to their team members
        // This would require fetching team member IDs first
        // For now, if no users specified, don't show any tasks
        baseConditions.push(where('userId', '==', ''));
        console.log("Manager user - no users specified, showing empty results");
      } else if (filters.isStaff && filters.currentScreen === 'Staff') {
        // Staff sees tasks assigned to them
        baseConditions.push(where('userId', '==', filters.currentUserId));
        console.log("Staff user - showing tasks for current user:", filters.currentUserId);
      }
    }

    const today = new Date();
    const todayStart = startOfDay(today);
    const todayEnd = endOfDay(today);

    // Get today's tasks
    const todayQuery = query(
      tasksRef,
      ...baseConditions,
      where('dueDate', '>=', Timestamp.fromDate(todayStart)),
      where('dueDate', '<=', Timestamp.fromDate(todayEnd))
    );
    const todaySnapshot = await getCountFromServer(todayQuery);

    // Get upcoming tasks (future)
    const upcomingQuery = query(
      tasksRef,
      ...baseConditions,
      where('dueDate', '>', Timestamp.fromDate(todayEnd))
    );
    const upcomingSnapshot = await getCountFromServer(upcomingQuery);

    // Get overdue tasks (past)
    const overdueQuery = query(
      tasksRef,
      ...baseConditions,
      where('dueDate', '<', Timestamp.fromDate(todayStart))
    );
    const overdueSnapshot = await getCountFromServer(overdueQuery);

    const dueToday = todaySnapshot.data().count;
    const upcoming = upcomingSnapshot.data().count;
    const overdue = overdueSnapshot.data().count;
    const totalTasks = dueToday + upcoming + overdue;

    return {
      totalTasks,
      dueToday,
      upcoming,
      overdue
    };
  } catch (error) {
    console.error('Error fetching task metrics:', error);
    throw error;
  }
};

// Helper function to fetch task metrics when more than 10 users are selected
const fetchTaskMetricsForManyUsers = async (filters: TaskFilters): Promise<TaskMetrics> => {
  try {
    const tasksRef = collection(db, `companies/${filters.companyId}/tasks`);
    
    // Fetch all pending tasks
    const allTasksQuery = query(
      tasksRef,
      where('isDone', '==', false),
      orderBy('dueDate', 'asc')
    );
    const allTasksSnapshot = await getDocs(allTasksQuery);
    
    // Filter by user IDs in memory
    const filteredTasks = allTasksSnapshot.docs.filter(doc => {
      const data = doc.data();
      return filters.userIds!.includes(data.userId);
    });

    const today = new Date();
    const todayStart = startOfDay(today);
    const todayEnd = endOfDay(today);

    let dueToday = 0;
    let upcoming = 0;
    let overdue = 0;

    filteredTasks.forEach(doc => {
      const data = doc.data();
      const dueDate = data.dueDate.toDate();
      
      if (dueDate < todayStart) {
        overdue++;
      } else if (dueDate >= todayStart && dueDate <= todayEnd) {
        dueToday++;
      } else {
        upcoming++;
      }
    });

    const totalTasks = dueToday + upcoming + overdue;

    return {
      totalTasks,
      dueToday,
      upcoming,
      overdue
    };
  } catch (error) {
    console.error('Error fetching task metrics for many users:', error);
    throw error;
  }
};

// Fetch detailed task items for a specific type
export const fetchTaskItems = async (
  filters: TaskFilters, 
  type: 'today' | 'upcoming' | 'overdue',
  limitCount: number = 100,
  lastDoc?: DocumentSnapshot
): Promise<{items: TaskItem[], lastDocument: DocumentSnapshot | null, hasMore: boolean}> => {
  try {
    const tasksRef = collection(db, `companies/${filters.companyId}/tasks`);
    
    // Determine if we need memory filtering for many users
    const needsMemoryFiltering = filters.userIds && filters.userIds.length > 10;
    const adjustedLimit = needsMemoryFiltering ? limitCount * 3 : limitCount;
    
    // Base conditions
    let conditions: QueryConstraint[] = [
      where('isDone', '==', false),
      orderBy('dueDate', 'asc'),
      limit(adjustedLimit)
    ];

    // Add pagination if lastDoc is provided
    if (lastDoc) {
      conditions.push(startAfter(lastDoc));
    }

    // Apply time-based filter conditions
    const today = new Date();
    const todayStart = startOfDay(today);
    const todayEnd = endOfDay(today);

    if (type === 'today') {
      conditions.push(
        where('dueDate', '>=', Timestamp.fromDate(todayStart)),
        where('dueDate', '<=', Timestamp.fromDate(todayEnd))
      );
    } else if (type === 'upcoming') {
      conditions.push(where('dueDate', '>', Timestamp.fromDate(todayEnd)));
    } else if (type === 'overdue') {
      conditions.push(where('dueDate', '<', Timestamp.fromDate(todayStart)));
    }

    // Apply user filters based on role and selection
    if (filters.userIds && filters.userIds.length > 0) {
      if (filters.userIds.length === 1) {
        conditions.push(where('userId', '==', filters.userIds[0]));
      } else if (filters.userIds.length <= 10) {
        conditions.push(where('userId', 'in', filters.userIds));
      }
      // For more than 10 users, we'll filter in memory after fetching
    } else {
      // Apply role-based filtering when no specific users selected
      if (filters.isAdmin && filters.currentScreen === 'Admin') {
        // Admin sees all tasks (no additional filter needed)
      } else if (filters.isManager && filters.currentScreen === 'Manager') {
        // Manager sees their team's tasks
        // For now, if no users specified, don't show any tasks
        conditions.push(where('userId', '==', ''));
      } else if (filters.isStaff && filters.currentScreen === 'Staff') {
        // Staff sees tasks assigned to them
        conditions.push(where('userId', '==', filters.currentUserId));
      }
    }

    console.log(`Fetching ${type} tasks with ${conditions.length} conditions`);

    const tasksQuery = query(tasksRef, ...conditions);
    const snapshot = await getDocs(tasksQuery);

    // Process task data
    const tasksData = await Promise.all(
      snapshot.docs.map(docSnapshot => processTaskData(docSnapshot, filters.companyId))
    );

    // Filter in memory if we have more than 10 users (Firebase 'in' query limitation)
    let filteredData = tasksData;
    if (needsMemoryFiltering && filters.userIds && filters.userIds.length > 10) {
      filteredData = tasksData.filter(item => filters.userIds!.includes(item.userId));
      // Trim to requested limit after filtering
      filteredData = filteredData.slice(0, limitCount);
    }

    // Check if there are more items available
    const hasMore = needsMemoryFiltering ? 
      (tasksData.length >= adjustedLimit && filteredData.length >= limitCount) : 
      snapshot.docs.length >= adjustedLimit;
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    console.log(`Fetched ${filteredData.length} ${type} tasks`);
    
    return {
      items: filteredData,
      lastDocument,
      hasMore
    };
  } catch (error) {
    console.error(`Error fetching ${type} task items:`, error);
    throw error;
  }
};
